/**
 * FamilyCircleTile — one face in the family circle for Nanna's spine
 * page or Grandad's heart page. Includes a small "listen" button that
 * plays a 3-second preview of that voice before Nanna/Grandad commits.
 *
 * Regular tap on the tile itself → commits (opens the session).
 * Tap on the small speaker icon (bottom-right) → plays 3s preview.
 */
import { motion } from "framer-motion";
import { Play, Pause } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const PREVIEW_SECONDS = 3;

// One shared preview element across the whole circle — tapping a new
// face automatically stops the previous preview.
const shared = { audio: null, currentId: null, timer: null };

function stopPreview() {
  if (shared.audio) {
    try { shared.audio.pause(); } catch (_) { /* noop */ }
    shared.audio = null;
  }
  if (shared.timer) { clearTimeout(shared.timer); shared.timer = null; }
  shared.currentId = null;
}

/**
 * Prime and START the healing-breath track synchronously inside the
 * user's tap gesture. Stashed on window so HeartImmersion/HealingImmersion
 * can adopt the SAME already-playing element after they mount — iOS
 * Safari holds the audio-unlock only for the current tick, so we can't
 * afford to wait for the child component's useEffect.
 */
function primeBreathOnTap() {
  try {
    if (window.__reclaimPrimedBreath && !window.__reclaimPrimedBreath.paused) {
      return; // already primed & playing
    }
    // MUST match the URL used inside HealingImmersion.jsx — the child
    // component reuses this pre-primed element. Any codec/URL drift
    // here shows up as a silent "NotSupportedError" once the child
    // tries to play. Feb 25 2026: aligned .m4a → .mp3 (Michael's mum
    // heard nothing but music because the primed m4a element was
    // rejected by Chromium/Safari and inherited by HealingImmersion).
    const a = new Audio(`${API}/api/audio/healing/healing_breath.mp3?v=20260225clean`);
    a.loop = false;   // we want THREE breaths only, then Daisy speaks
    a.volume = 0.55;
    a.preload = 'auto';
    try { a.load(); } catch (_) { /* noop */ }
    a.play().catch(() => { /* iOS blocked — the child will retry */ });
    window.__reclaimPrimedBreath = a;
  } catch (_) { /* noop */ }
}

export default function FamilyCircleTile({
  voice,
  index,
  previewSrc,          // URL to preview from
  onPick,
  testIdPrefix,
}) {
  const [playing, setPlaying] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => () => {
    mountedRef.current = false;
    if (shared.currentId === voice.id) stopPreview();
  }, [voice.id]);

  const handlePreview = (e) => {
    e.stopPropagation();
    // If this tile is already previewing → stop
    if (shared.currentId === voice.id) {
      stopPreview();
      setPlaying(false);
      return;
    }
    stopPreview();
    try {
      const a = new Audio(previewSrc);
      a.preload = "auto";
      a.volume = 0.9;
      shared.audio = a;
      shared.currentId = voice.id;
      a.addEventListener("ended", () => {
        if (mountedRef.current) setPlaying(false);
        stopPreview();
      });
      a.play().then(() => {
        if (mountedRef.current) setPlaying(true);
        shared.timer = setTimeout(() => {
          try { a.pause(); } catch (_) { /* noop */ }
          if (mountedRef.current) setPlaying(false);
          stopPreview();
        }, PREVIEW_SECONDS * 1000);
      }).catch(() => {
        stopPreview();
        if (mountedRef.current) setPlaying(false);
      });
    } catch (_) {
      stopPreview();
      setPlaying(false);
    }
  };

  return (
    <motion.div
      data-testid={`${testIdPrefix}-${voice.speaker_name.replace(/\W+/g, "-").toLowerCase()}`}
      onClick={() => { stopPreview(); primeBreathOnTap(); onPick(voice); }}
      role="button"
      tabIndex={0}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className="flex flex-col items-center gap-2 relative cursor-pointer select-none"
    >
      <div
        className="relative flex items-center justify-center rounded-full overflow-hidden"
        style={{
          width: 104,
          height: 104,
          background: `radial-gradient(circle at 50% 42%, ${voice.accent} 0%, ${voice.accent}80 40%, ${voice.accent}20 75%, transparent 100%)`,
          boxShadow: `0 0 40px ${voice.accent}55, inset 0 4px 18px rgba(255,240,210,0.35), inset 0 -4px 18px rgba(80,45,20,0.4)`,
        }}
      >
        <motion.span
          className="absolute inset-0 rounded-full"
          style={{ border: "1px solid rgba(255, 224, 170, 0.4)" }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.5, 0.15, 0.5] }}
          transition={{ duration: 3 + index * 0.4, repeat: Infinity, ease: "easeInOut" }}
        />
        <span
          className="text-3xl text-[#3a1e08]"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 600 }}
        >
          {voice.speaker_name[0]}
        </span>
        {/* Preview button — small pill in the bottom-right of the tile.
            Kept out of the way so the primary "tap to enter" gesture is
            still the face itself. */}
        <button
          type="button"
          onClick={handlePreview}
          data-testid={`${testIdPrefix}-${voice.speaker_name.replace(/\W+/g, "-").toLowerCase()}-preview`}
          aria-label={`Preview ${voice.speaker_name}'s voice`}
          className="absolute bottom-1 right-1 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
          style={{
            background: "rgba(20,10,4,0.75)",
            border: "1px solid rgba(255,224,170,0.35)",
            color: "#f5deb3",
          }}
        >
          {playing ? <Pause size={12} strokeWidth={2.4} /> : <Play size={12} strokeWidth={2.4} />}
        </button>
      </div>
      <div className="flex flex-col gap-0.5">
        <div
          className="text-amber-50 text-base leading-tight"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          {voice.speaker_name}
        </div>
        <div className="text-white/40 text-xs italic">{voice.relation}</div>
      </div>
    </motion.div>
  );
}
